import { useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Clock, ShieldAlert, ShieldX } from 'lucide-react';
import { PillButton } from '@ab-platform/ui/pill-button';
import { FocusedPageShell } from '@ab-platform/ui/focused-page-shell';
import { IdentityVerificationStep } from '@/components/IdentityVerificationStep';
import {
  clearDocumentsPageStateFromSession,
  loadDocumentsPageStateFromAllSources,
  type DocumentsPageState,
} from '@/lib/documents-state-recovery';
import {
  clearIdentityVerificationReturnContext,
  loadIdentityVerificationReturnContext,
  saveIdentityVerificationReturnContext,
} from '@/lib/onboardingReturnContext';
import {
  diditReturnOutcome,
  readDiditReturnStatus,
  type DiditReturnTone,
} from '@/lib/diditReturnStatus';

/**
 * Dedicated post-submit identity-verification step (PR-DIDIT-6B).
 *
 * Sits BETWEEN the submit and the documents upload. Reuses the exact same
 * post-submit state (uploadToken + legalNature + orgType + submissionId) and
 * the same recovery channels as the documents page (router state > URL hash >
 * sessionStorage), so a hard refresh / support URL keeps working. "Continua ai
 * documenti" forwards the same state to `/documenti` — the token never lands in
 * the URL / query string.
 *
 * Product decision unchanged: KYC stays POST-submit, token-gated, advisory. The
 * upload is not technically blocked here; the conversion gate is in the
 * backoffice (DIDIT-7/-10).
 */

/**
 * Didit-style "verified" mark — a viewfinder frame with a check, matching the
 * provider's completion screen. Color is inherited via `currentColor`.
 */
function ScanCheckIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="M4 8V6a2 2 0 0 1 2-2h2" />
      <path d="M4 16v2a2 2 0 0 0 2 2h2" />
      <path d="M16 4h2a2 2 0 0 1 2 2v2" />
      <path d="M16 20h2a2 2 0 0 0 2-2v-2" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

export function VerificaIdentitaPage(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  // Recovery order: router state > URL hash > sessionStorage (existing), then
  // the localStorage return-context — a mobile-safe fallback for the Didit
  // round-trip, which drops in-memory state + sessionStorage on many phones.
  const state = useMemo<DocumentsPageState | null>(() => {
    const fromExisting = loadDocumentsPageStateFromAllSources(
      location.state,
      window.location.hash,
    );
    if (fromExisting) return fromExisting;
    return loadIdentityVerificationReturnContext();
  }, [location.key]);

  // Persist a short-lived recovery context as soon as we have a valid state, so
  // a redirect OUT to Didit and back can restore the token even when the mobile
  // browser drops router state + sessionStorage. The token never enters the URL.
  useEffect(() => {
    if (!state) return;
    saveIdentityVerificationReturnContext({
      uploadToken: state.uploadToken,
      legalNature: state.legalNature,
      orgType: state.orgType,
      requestPublicId: state.submissionId,
    });
  }, [state]);

  if (state === null) {
    // No recoverable applicant session. If Didit redirected here with a result
    // (`?status=…`, a non-secret param) show a clean status-aware courtesy page
    // — operator/QR cross-device or a standalone re-verification link. Otherwise
    // the link is lost/expired → invite a fresh scan. Never reads the token.
    const outcome = diditReturnOutcome(readDiditReturnStatus(location.search));
    const toneText: Record<DiditReturnTone, string> = {
      success: 'text-state-success',
      info: 'text-state-info',
      danger: 'text-state-danger',
      warning: 'text-state-warning',
    };
    const iconClassName = `h-16 w-16 ${toneText[outcome.tone]}`;
    return (
      <div className="mx-auto flex min-h-full w-full max-w-focused flex-col items-center justify-center gap-8 px-4 py-12 text-center sm:px-6">
        <div className="flex flex-col items-center gap-4">
          {outcome.kind === 'approved' ? (
            <ScanCheckIcon className={iconClassName} />
          ) : outcome.kind === 'review' ? (
            <Clock className={iconClassName} aria-hidden />
          ) : outcome.kind === 'failed' ? (
            <ShieldX className={iconClassName} aria-hidden />
          ) : (
            <ShieldAlert className={iconClassName} aria-hidden />
          )}
          <h1 className="text-heading1 text-foreground">
            {outcome.title}
          </h1>
          <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
            {outcome.message}
          </p>
        </div>
        {outcome.showReturnCta ? (
          <PillButton asChild size="lg" className="w-full max-w-focused-toolbar">
            <Link to="/registrazione-intermediario">Torna al modulo</Link>
          </PillButton>
        ) : null}
      </div>
    );
  }

  const handleSessionExpired = () => {
    // Token rejected by the BE → drop every recovery channel (incl. the
    // localStorage return-context) so we don't loop on a dead token.
    clearDocumentsPageStateFromSession();
    clearIdentityVerificationReturnContext();
    navigate('/registrazione-intermediario/verifica-identita', {
      replace: true,
      state: null,
    });
  };

  const goToDocuments = () => {
    // Proceeding to the documents step: the Didit round-trip recovery context
    // is no longer needed — clear it (avoid a lingering token in localStorage).
    // Token stays out of the URL; sessionStorage already mirrors the state.
    clearIdentityVerificationReturnContext();
    navigate('/registrazione-intermediario/documenti', {
      replace: true,
      state,
    });
  };

  return (
    <FocusedPageShell
      title="Verifica la tua identità"
      description="Prima di allegare i documenti, completa la verifica identità. Ti guideremo noi nei passaggi richiesti."
      footer={null}
    >
      {state.submissionId ? (
        <div className="grid gap-0.5 rounded-md bg-foreground/5 px-3 py-2 text-xs">
          <span className="text-muted-foreground">Riferimento richiesta</span>
          <span className="font-mono text-foreground">{state.submissionId}</span>
        </div>
      ) : null}
      <IdentityVerificationStep
        uploadToken={state.uploadToken}
        onContinue={goToDocuments}
        onSessionExpired={handleSessionExpired}
      />
    </FocusedPageShell>
  );
}
